import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonLeftSidebar,
    SkeletonLeftSidebarLayout,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack
} from 'src/components/widgets/skeleton';
import { TREE_SIDEBAR_WIDTH } from 'src/components/widgets/sidebar/commonStyleSideBar';
import { ProductsListPageSkeleton } from '../placementProduct/placementProductSkeleton';

const TemplatesSkeletonShell = styled(SkeletonPageShell)``;

const TemplatesSkeletonHeader = styled(SkeletonPageHeader)``;

const TemplatesListSkeletonLayout = styled(SkeletonLeftSidebarLayout)<{ $embedded?: boolean }>`
    gap: ${(props) => (props.$embedded ? '0' : '24px')};
    height: ${(props) => (props.$embedded ? '100vh' : 'auto')};
    overflow: ${(props) => (props.$embedded ? 'hidden' : 'visible')};

    @media (max-width: 900px) {
        display: ${(props) => (props.$embedded ? 'flex' : 'block')};
    }
`;

const getTemplatesSidebarWidth = (embedded?: boolean) => (embedded ? '250px' : `${TREE_SIDEBAR_WIDTH}px`);

const TemplatesSidebarSkeleton = styled(SkeletonLeftSidebar)<{ $embedded?: boolean }>`
    flex: 0 0 ${(props) => getTemplatesSidebarWidth(props.$embedded)};
    width: ${(props) => getTemplatesSidebarWidth(props.$embedded)};
    min-width: ${(props) => getTemplatesSidebarWidth(props.$embedded)};
    padding: ${(props) => (props.$embedded ? '20px' : '0')};
    background: ${(props) => (props.$embedded ? '#ffffff' : 'transparent')};
    border: 0;
    border-right: ${(props) => (props.$embedded ? `1px solid ${props.theme.app.borderColor}` : '0')};
    border-radius: 0;
    overflow: hidden;

    @media (max-width: 900px) {
        display: ${(props) => (props.$embedded ? 'block' : 'none')};
    }
`;

const TemplatesListContentSkeleton = styled.div<{ $embedded?: boolean }>`
    flex: 1;
    min-width: 0;
    overflow-y: ${(props) => (props.$embedded ? 'auto' : 'visible')};
    padding: ${(props) => (props.$embedded ? '20px' : '0')};
`;

const TemplatesSidebarHeaderSkeleton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 12px;
`;

const TemplatesSidebarItemSkeleton = styled.div<{ $indent?: boolean; $selected?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    margin-bottom: 4px;
    padding: 6px 9px 6px ${(props) => (props.$indent ? '24px' : '9px')};
    border-radius: 8px;
    background: ${(props) => (props.$selected ? 'rgb(0 0 0 / 5%)' : 'transparent')};
`;

const TemplatesGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 18px;
    min-width: 0;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const TemplateCardSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    padding: 12px;
    min-width: 0;
`;

const TemplateTaskSectionSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    padding: 24px;
    min-width: 0;
`;

const TemplateOverviewGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    min-width: 0;
`;

const TemplateResultsTableSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;
    min-width: 0;
`;

const TemplateResultsRowSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 1fr) 120px 120px 96px;
    gap: 18px;
    align-items: center;
    min-height: 58px;
    padding: 14px 18px;
    border-bottom: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    min-width: 0;

    &:last-child {
        border-bottom: 0;
    }

    @media (max-width: 700px) {
        grid-template-columns: minmax(0, 1fr) 96px;
    }
`;

const TemplateResultsOptionalColumn = styled.div`
    @media (max-width: 700px) {
        display: none;
    }
`;

const TemplatesSidebarContentSkeleton: FC<{ embedded?: boolean }> = ({ embedded = false }) => (
    <TemplatesSidebarSkeleton $embedded={embedded}>
        <TemplatesSidebarHeaderSkeleton>
            <SkeletonBlock $width='112px' $height='16px' />
            <SkeletonRow $gap='6px' $margin='0 0 0 auto'>
                <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
            </SkeletonRow>
        </TemplatesSidebarHeaderSkeleton>
        <SkeletonStack $gap='2px'>
            <TemplatesSidebarItemSkeleton $selected>
                <SkeletonBlock $width='150px' $height='13px' />
            </TemplatesSidebarItemSkeleton>
            {Array.from({ length: 4 }).map((_, macroIndex) => (
                <React.Fragment key={macroIndex}>
                    <TemplatesSidebarItemSkeleton>
                        <SkeletonBlock $width={macroIndex % 2 === 0 ? '130px' : '105px'} $height='13px' />
                        <SkeletonRow $gap='4px' $margin='0 0 0 auto'>
                            <SkeletonBlock $width='24px' $height='24px' $radius='4px' />
                            <SkeletonBlock $width='24px' $height='24px' $radius='4px' />
                        </SkeletonRow>
                    </TemplatesSidebarItemSkeleton>
                    {Array.from({ length: macroIndex % 2 === 0 ? 2 : 1 }).map((_, categoryIndex) => (
                        <TemplatesSidebarItemSkeleton key={categoryIndex} $indent>
                            <SkeletonBlock $width={categoryIndex % 2 === 0 ? '120px' : '92px'} $height='13px' />
                            <SkeletonRow $gap='4px' $margin='0 0 0 auto'>
                                <SkeletonBlock $width='24px' $height='24px' $radius='4px' />
                                <SkeletonBlock $width='24px' $height='24px' $radius='4px' />
                            </SkeletonRow>
                        </TemplatesSidebarItemSkeleton>
                    ))}
                </React.Fragment>
            ))}
            <TemplatesSidebarItemSkeleton>
                <SkeletonBlock $width='68px' $height='13px' />
            </TemplatesSidebarItemSkeleton>
        </SkeletonStack>
    </TemplatesSidebarSkeleton>
);

const ProductItemTemplatesContentSkeleton: FC = () => (
    <SkeletonStack $gap='18px'>
        <SkeletonRow $gap='16px' $margin='0 0 4px'>
            <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
            <SkeletonBlock $width='min(420px, 100%)' $height='14px' />
        </SkeletonRow>
        <TemplatesGridSkeleton>
            {Array.from({ length: 8 }).map((_, index) => (
                <TemplateCardSkeleton key={index}>
                    <SkeletonStack $gap='10px'>
                        <SkeletonBlock $height='150px' $radius='6px' />
                        <SkeletonBlock $width={index % 2 === 0 ? '78%' : '56%'} $height='15px' />
                        <SkeletonBlock $width='48%' $height='12px' />
                    </SkeletonStack>
                </TemplateCardSkeleton>
            ))}
        </TemplatesGridSkeleton>
    </SkeletonStack>
);

export const ProductTemplatesPageSkeleton: FC = () => <ProductsListPageSkeleton titleWidth='250px' actionWidth='' />;

export const ProductItemTemplatesPageSkeleton: FC = () => {
    const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === 'true';

    if (isEmbedded) {
        return (
            <TemplatesListSkeletonLayout aria-hidden='true' $embedded>
                <TemplatesSidebarContentSkeleton embedded />
                <TemplatesListContentSkeleton $embedded>
                    <SkeletonRow $gap='8px' $justify='flex-end' $margin='0 0 16px'>
                        <SkeletonBlock $width='148px' $height='36px' $radius='6px' />
                        <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
                    </SkeletonRow>
                    <ProductItemTemplatesContentSkeleton />
                </TemplatesListContentSkeleton>
            </TemplatesListSkeletonLayout>
        );
    }

    return (
        <TemplatesSkeletonShell aria-hidden='true'>
            <TemplatesSkeletonHeader>
                <SkeletonStack $gap='8px' $flex='1'>
                    <SkeletonBlock $width='min(260px, 100%)' $height='26px' />
                    <SkeletonBlock $width='min(420px, 100%)' $height='13px' />
                </SkeletonStack>
                <SkeletonRow $gap='8px'>
                    <SkeletonBlock $width='148px' $height='36px' $radius='6px' />
                    <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
                </SkeletonRow>
            </TemplatesSkeletonHeader>
            <TemplatesListSkeletonLayout>
                <TemplatesSidebarContentSkeleton />
                <TemplatesListContentSkeleton>
                    <ProductItemTemplatesContentSkeleton />
                </TemplatesListContentSkeleton>
            </TemplatesListSkeletonLayout>
        </TemplatesSkeletonShell>
    );
};

export const TemplateTaskDetailPageSkeleton: FC = () => (
    <TemplatesSkeletonShell aria-hidden='true'>
        <TemplatesSkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(180px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(420px, 100%)' $height='13px' />
            </SkeletonStack>
        </TemplatesSkeletonHeader>
        <SkeletonStack $gap='18px'>
            <TemplateTaskSectionSkeleton>
                <SkeletonBlock $width='110px' $height='20px' $margin='0 0 18px' />
                <TemplateOverviewGridSkeleton>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <SkeletonStack key={index} $gap='8px'>
                            <SkeletonBlock $width='76px' $height='12px' />
                            <SkeletonBlock $width={index % 2 === 0 ? '120px' : '90px'} $height='18px' />
                        </SkeletonStack>
                    ))}
                </TemplateOverviewGridSkeleton>
            </TemplateTaskSectionSkeleton>
            <TemplateTaskSectionSkeleton>
                <SkeletonStack $gap='12px'>
                    <SkeletonBlock $width='120px' $height='20px' />
                    <SkeletonBlock $height='12px' $radius='6px' />
                    <SkeletonBlock $width='min(520px, 100%)' $height='44px' $radius='6px' />
                </SkeletonStack>
            </TemplateTaskSectionSkeleton>
            <TemplateResultsTableSkeleton>
                {Array.from({ length: 6 }).map((_, index) => (
                    <TemplateResultsRowSkeleton key={index}>
                        <SkeletonBlock $width={index % 2 === 0 ? '70%' : '56%'} $height='15px' />
                        <TemplateResultsOptionalColumn>
                            <SkeletonBlock $width='84px' $height='13px' />
                        </TemplateResultsOptionalColumn>
                        <TemplateResultsOptionalColumn>
                            <SkeletonBlock $width='74px' $height='22px' $radius='11px' />
                        </TemplateResultsOptionalColumn>
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                    </TemplateResultsRowSkeleton>
                ))}
            </TemplateResultsTableSkeleton>
        </SkeletonStack>
    </TemplatesSkeletonShell>
);
